// nettiSound — netti's bite-sized sounds, all CODE, no files.
//
// THE REGISTER (John, 2026-08-31): ambient. Quiet, soft-edged, a little
// breathy — a presence in the room rather than a notification. The first pass
// had four sounds that were "too loud, thick or abrupt" (a noise-burst tick, a
// bright two-note ping, a glass chime, a hard-attacked blip); they are gone.
// What survives — think · oops · absorb · radiate — set the law every sound now
// obeys:
//
//   • ATTACK ≥ ~0.03s. Nothing starts instantly; a hard onset is what reads as
//     a click, and a click is a notification, not a creature.
//   • LOWPASS everything. The top octave is where "thick" and "cheap" live.
//   • VOLUME ≤ ~0.26, and the small gestures far below that.
//   • MOVEMENT over impact — a slide, a swell, a breath. Never a hit.
//   • Partials stay faint (≤0.1) and short; the film's 2.76×/5.4× glass DNA is
//     a colour here, not a bell.
//
// Rendered sample-by-sample into one AudioBuffer (the ZzFX school: the whole
// palette is a handful of numbers and weighs nothing), through ONE AudioContext
// shared with the voice box — so netti's body can watch everything it emits on
// a single analyser. Nothing auto-plays; a sound follows a user gesture or
// netti speaking. Tunable live at /test/netti-voice.

export type SoundLayer = {
  /** Offset into the sound, seconds. */
  at?: number;
  /** Oscillator family. Noise ignores tonality but keeps the envelope. */
  shape?: "sine" | "triangle" | "noise";
  /** Start frequency, Hz. */
  freq: number;
  /** Frequency at the end of the layer (linear slide). Defaults to freq. */
  endFreq?: number;
  /** Envelope, seconds. Keep attack ≥ ~0.03 — see the register above. */
  attack?: number;
  sustain?: number;
  release?: number;
  /** Inharmonic overtone: ratio (the film's glass is 2.76 / 5.4) + its gain. */
  partial?: number;
  partialGain?: number;
  /** Gentle vibrato — depth as a fraction of freq, rate in Hz. */
  vibrato?: number;
  vibratoHz?: number;
  /** One-pole lowpass openness 0..1 (1 = fully open). Lower = softer. */
  lowpass?: number;
  /** Layer gain 0..1. */
  volume?: number;
};

export type NettiSound = {
  label: string;
  hint: string;
  layers: SoundLayer[];
};

// ── the palette ──────────────────────────────────────────────────────────────
// Named as gestures, never as audio jargon, so an expression can reference one
// directly. The four John kept are unchanged; the four he cut were rebuilt in
// their register.
export const NETTI_SOUNDS: Record<string, NettiSound> = {
  // REBUILT — was a hard 1240Hz blip. Now a low breath that rises and opens.
  notice: {
    label: "notice",
    hint: "a soft rise — something registered",
    layers: [
      { shape: "sine", freq: 396, endFreq: 462, attack: 0.13, sustain: 0.04, release: 0.34, partial: 2.76, partialGain: 0.05, lowpass: 0.4, volume: 0.16 },
      { at: 0.04, shape: "noise", freq: 0, attack: 0.12, release: 0.24, lowpass: 0.09, volume: 0.05 },
    ],
  },
  // REBUILT — was two bright pings. Now two overlapping blooms that swell into
  // each other; the interval survives, the attack doesn't.
  hello: {
    label: "hello",
    hint: "two soft blooms — netti wakes",
    layers: [
      { shape: "sine", freq: 392, attack: 0.16, sustain: 0.03, release: 0.42, lowpass: 0.42, volume: 0.15 },
      { at: 0.19, shape: "sine", freq: 588, attack: 0.18, sustain: 0.04, release: 0.5, partial: 2.76, partialGain: 0.06, lowpass: 0.46, volume: 0.14 },
      { at: 0.02, shape: "noise", freq: 0, attack: 0.16, release: 0.3, lowpass: 0.08, volume: 0.045 },
    ],
  },
  // NEW (2026-10-15) — the moment netti can hear. A breath in: two soft notes
  // that each lean upward and overlap (a rising fourth, lower and quieter than
  // hello's two blooms), under a little air that opens with them. It plays once,
  // after the person says yes to the microphone, so it must sit lower than every
  // sound that announces netti: you are being listened for, not greeted.
  listen: {
    label: "listen",
    hint: "a soft rising pair, a breath in: netti can hear",
    layers: [
      { shape: "sine", freq: 330, endFreq: 372, attack: 0.15, sustain: 0.02, release: 0.34, lowpass: 0.36, volume: 0.1 },
      { at: 0.16, shape: "sine", freq: 440, endFreq: 496, attack: 0.17, sustain: 0.03, release: 0.46, partial: 2.76, partialGain: 0.04, lowpass: 0.4, volume: 0.085 },
      { at: 0.02, shape: "noise", freq: 0, attack: 0.2, release: 0.3, lowpass: 0.07, volume: 0.03 },
    ],
  },
  // KEPT — John's reference for the whole register.
  think: {
    label: "think",
    hint: "a low airy hum",
    layers: [
      { shape: "triangle", freq: 196, attack: 0.12, sustain: 0.1, release: 0.34, vibrato: 0.012, vibratoHz: 5.4, lowpass: 0.28, volume: 0.22 },
    ],
  },
  // REBUILT — was a glass chime with a bright 5.4× partial on top. Now a tone
  // that settles downward and lets go.
  done: {
    label: "done",
    hint: "a quiet settling",
    layers: [
      { shape: "sine", freq: 654, endFreq: 522, attack: 0.14, sustain: 0.05, release: 0.52, partial: 2.76, partialGain: 0.07, lowpass: 0.46, volume: 0.18 },
    ],
  },
  // REBUILT — was a filtered noise tick (the most abrupt of all). Now the
  // faintest low tap, barely above the room.
  touch: {
    label: "touch",
    hint: "the faintest tap",
    layers: [
      { shape: "sine", freq: 288, endFreq: 264, attack: 0.035, release: 0.17, lowpass: 0.3, volume: 0.1 },
    ],
  },
  // KEPT.
  oops: {
    label: "oops",
    hint: "a gentle downward slide",
    layers: [
      { shape: "sine", freq: 460, endFreq: 330, attack: 0.01, release: 0.26, vibrato: 0.008, vibratoHz: 7, volume: 0.28 },
    ],
  },
  // KEPT.
  radiate: {
    label: "radiate",
    hint: "a slow luminous swell",
    layers: [
      { shape: "sine", freq: 520, endFreq: 660, attack: 0.26, release: 0.4, partial: 2.76, partialGain: 0.14, vibrato: 0.006, vibratoHz: 4.5, volume: 0.26 },
    ],
  },
  // KEPT.
  absorb: {
    label: "absorb",
    hint: "the swell, breathing back in",
    layers: [
      { shape: "sine", freq: 660, endFreq: 420, attack: 0.2, release: 0.42, lowpass: 0.5, volume: 0.22 },
    ],
  },
};

export type NettiSoundName = keyof typeof NETTI_SOUNDS;

/** Display order — the four rebuilt first, so a listen walks the new register;
 *  the newest (listen) last. */
export const NETTI_SOUND_ORDER: NettiSoundName[] = [
  "notice", "hello", "done", "touch", "think", "oops", "radiate", "absorb", "listen",
];

// ── the renderer ─────────────────────────────────────────────────────────────

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let analyser: AnalyserNode | null = null;
let level = 0.9;

function ensureCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = level;
    // EVERYTHING netti emits passes through this analyser on its way out — the
    // sounds above and the voice box alike. That is what lets netti's body react
    // to its own audio identically, whatever made it (see lib/netti/audioBody).
    analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.55;
    master.connect(analyser);
    analyser.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Shared context — the voice box rides the same one so levels stay coherent. */
export function nettiAudioContext(): AudioContext | null {
  return ensureCtx();
}
export function nettiMasterOut(): GainNode | null {
  ensureCtx();
  return master;
}
/** The one analyser every netti sound passes through. */
export function nettiAnalyser(): AnalyserNode | null {
  ensureCtx();
  return analyser;
}

export function setNettiSoundLevel(v: number) {
  level = Math.min(1, Math.max(0, v));
  if (master) master.gain.value = level;
}
export function getNettiSoundLevel() {
  return level;
}

function renderLayer(l: SoundLayer, sampleRate: number): Float32Array {
  const attack = l.attack ?? 0.03;
  const sustain = l.sustain ?? 0;
  const release = l.release ?? 0.2;
  const dur = attack + sustain + release;
  const n = Math.max(1, Math.floor(dur * sampleRate));
  const out = new Float32Array(n);
  const vol = l.volume ?? 0.2;
  const f0 = l.freq;
  const f1 = l.endFreq ?? l.freq;
  const partial = l.partial ?? 0;
  const partialGain = l.partialGain ?? 0;
  const vib = l.vibrato ?? 0;
  const vibHz = l.vibratoHz ?? 5;
  const lp = l.lowpass ?? 1;
  let phase = 0;
  let phaseP = 0;
  let filt = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const p = t / dur;
    // envelope: cosine-eased IN as well as out — a linear attack still has a
    // corner at t=0, and a corner is the click we are designing against.
    let env: number;
    if (t < attack) env = 0.5 - 0.5 * Math.cos((t / attack) * Math.PI);
    else if (t < attack + sustain) env = 1;
    else env = 0.5 + 0.5 * Math.cos(((t - attack - sustain) / release) * Math.PI);
    const f = (f0 + (f1 - f0) * p) * (1 + vib * Math.sin(2 * Math.PI * vibHz * t));
    let s: number;
    if (l.shape === "noise") {
      s = Math.random() * 2 - 1;
    } else {
      phase += (2 * Math.PI * f) / sampleRate;
      s = l.shape === "triangle" ? Math.asin(Math.sin(phase)) * (2 / Math.PI) : Math.sin(phase);
      if (partial > 0 && partialGain > 0) {
        phaseP += (2 * Math.PI * f * partial) / sampleRate;
        // the partial rings shorter than the fundamental — a colour, not a bell
        s += Math.sin(phaseP) * partialGain * Math.max(0, 1 - p * 1.6);
      }
    }
    // one-pole lowpass; lp 1 leaves the sample untouched
    filt += (s - filt) * Math.min(1, lp);
    s = lp >= 1 ? s : filt;
    out[i] = Math.tanh(s * env * vol * 1.4);
  }
  return out;
}

/** Render a sound (by name or ad-hoc definition) into an AudioBuffer. */
export function renderNettiSound(sound: NettiSound | NettiSoundName, ac: AudioContext): AudioBuffer {
  const def = typeof sound === "string" ? NETTI_SOUNDS[sound] : sound;
  const sr = ac.sampleRate;
  const rendered = def.layers.map((l) => ({ at: Math.floor((l.at ?? 0) * sr), data: renderLayer(l, sr) }));
  const total = Math.max(...rendered.map((r) => r.at + r.data.length), 1);
  const buf = ac.createBuffer(1, total, sr);
  const ch = buf.getChannelData(0);
  for (const r of rendered) for (let i = 0; i < r.data.length; i++) ch[r.at + i] += r.data[i];
  return buf;
}

const bufferCache = new Map<string, AudioBuffer>();

/** Play a palette sound. Safe to call anywhere — silently no-ops without audio. */
export function playNettiSound(name: NettiSoundName, opts?: { volume?: number }) {
  const ac = ensureCtx();
  if (!ac || !master || !NETTI_SOUNDS[name]) return;
  let buf = bufferCache.get(name);
  if (!buf) {
    buf = renderNettiSound(name, ac);
    bufferCache.set(name, buf);
  }
  const src = ac.createBufferSource();
  src.buffer = buf;
  if (opts?.volume !== undefined && opts.volume !== 1) {
    const g = ac.createGain();
    g.gain.value = Math.min(1, Math.max(0, opts.volume));
    src.connect(g);
    g.connect(master);
  } else {
    src.connect(master);
  }
  src.start();
}

/** Drop cached renders (the lab calls this after editing a sound's numbers). */
export function invalidateNettiSoundCache(name?: NettiSoundName) {
  if (name) bufferCache.delete(name);
  else bufferCache.clear();
}
